/**
 * EuroLeague clubs: the one table both the team registry and the EuroLeague service read.
 */

/**
 * Odds API team name → EuroLeague API name + abbreviation + crest.
 *
 * `aliases` are other spellings a provider has actually sent for the club. Every name here
 * resolves exactly through the team registry; a club is never matched by a substring,
 * which is how an unknown team silently became another club.
 */
export const EUROLEAGUE_TEAMS: Record<
  string,
  { euroleagueName: string; code: string; crestUrl: string; aliases?: readonly string[] }
> = {
  Fenerbahce: {
    euroleagueName: 'Fenerbahce Tarfin Istanbul',
    code: 'ULK',
    crestUrl: 'https://media-cdn.cortextech.io/3b7f020e-5b39-49a1-b4b2-efea918edab7.png',
    aliases: ['Fenerbahce SK', 'Fenerbahce Beko Istanbul'],
  },
  Valencia: {
    euroleagueName: 'Valencia Basket',
    code: 'PAM',
    crestUrl: 'https://media-cdn.cortextech.io/d88f3c71-1519-4b19-8cfb-99e26a4c008e.png',
  },
  'Olympiacos Piraeus': {
    euroleagueName: 'Olympiacos Piraeus',
    code: 'OLY',
    crestUrl: 'https://media-cdn.incrowdsports.com/789423ac-3cdf-4b89-b11c-b458aa5f59a6.png',
    aliases: ['Olympiacos'],
  },
  'Real Madrid': {
    euroleagueName: 'Real Madrid',
    code: 'MAD',
    crestUrl: 'https://media-cdn.incrowdsports.com/371b0d9b-9250-4c09-bda7-0686cf024657.png',
  },
  'Hapoel Tel Aviv': {
    euroleagueName: 'Hapoel IBI Tel Aviv',
    code: 'HTA',
    crestUrl: 'https://media-cdn.incrowdsports.com/cbb1c3ad-03d5-426a-b5ef-2832a4eee484.png',
  },
  Zalgiris: {
    euroleagueName: 'Zalgiris Kaunas',
    code: 'ZAL',
    crestUrl: 'https://media-cdn.incrowdsports.com/0aa09358-3847-4c4e-b228-3582ee4e536d.png',
  },
  'Crvena Zvezda': {
    euroleagueName: 'Crvena Zvezda Meridianbet Belgrade',
    code: 'RED',
    crestUrl: 'https://media-cdn.incrowdsports.com/d2eef4a8-62df-4fdd-9076-276004268515.png',
    aliases: ['KK Crvena zvezda'],
  },
  Barcelona: {
    euroleagueName: 'FC Barcelona',
    code: 'BAR',
    crestUrl: 'https://media-cdn.incrowdsports.com/35dfa503-e417-481f-963a-bdf6f013763e.png',
    aliases: ['FC Barcelona Bàsquet'],
  },
  Monaco: {
    euroleagueName: 'AS Monaco',
    code: 'MCO',
    crestUrl: 'https://media-cdn.incrowdsports.com/89ed276a-2ba3-413f-8ea2-b3be209ca129.png',
  },
  Panathinaikos: {
    euroleagueName: 'Panathinaikos AKTOR Athens',
    code: 'PAN',
    crestUrl: 'https://media-cdn.incrowdsports.com/e3dff28a-9ec6-4faf-9d96-ecbc68f75780.png',
  },
  Dubai: {
    euroleagueName: 'Dubai Basketball',
    code: 'DUB',
    crestUrl: 'https://media-cdn.incrowdsports.com/1efae090-16e2-4963-ae47-4b94f249c244.png',
  },
  Milan: {
    euroleagueName: 'Armani Olimpia Milan',
    code: 'MIL',
    crestUrl: 'https://media-cdn.cortextech.io/9512ee73-a0f1-4647-a01e-3c2938aba6b8.png',
    aliases: ['EA7 Emporio Armani Milan', 'Pallacanestro Olimpia Milano'],
  },
  'Maccabi Tel Aviv': {
    euroleagueName: 'Maccabi Rapyd Tel Aviv',
    code: 'TEL',
    crestUrl: 'https://media-cdn.cortextech.io/1b533342-78f5-4932-b714-a7d80b5826b5.png',
  },
  'Virtus Bologna': {
    euroleagueName: 'Virtus Bologna',
    code: 'VIR',
    crestUrl: 'https://media-cdn.cortextech.io/1362801d-dd09-4fd0-932d-ead56063ab77.png',
    aliases: ['Virtus Segafredo Bologna'],
  },
  'Bayern Munich': {
    euroleagueName: 'FC Bayern Munich',
    code: 'MUN',
    crestUrl: 'https://media-cdn.incrowdsports.com/817b0e58-d595-4b09-ab0b-1e7cc26249ff.png',
    aliases: ['FC Bayern München'],
  },
  'Paris Basketball': {
    euroleagueName: 'Paris Basketball',
    code: 'PRS',
    crestUrl: 'https://media-cdn.incrowdsports.com/a033e5b3-0de7-48a3-98d9-d9a4b9df1f39.png',
  },
  'Anadolu Efes': {
    euroleagueName: 'Anadolu Efes Istanbul',
    code: 'IST',
    crestUrl: 'https://media-cdn.cortextech.io/9a463aa2-ceb2-481c-9a95-1cddee0a248e.png',
  },
  Baskonia: {
    euroleagueName: 'Kosner Baskonia Vitoria-Gasteiz',
    code: 'BAS',
    crestUrl: 'https://media-cdn.cortextech.io/cbc49cb0-99ce-4462-bdb7-56983ee03cf4.png',
    aliases: ['Saski Baskonia'],
  },
  Partizan: {
    euroleagueName: 'Partizan Mozzart Bet Belgrade',
    code: 'PAR',
    crestUrl: 'https://media-cdn.incrowdsports.com/2681304e-77dd-4331-88b1-683078c0fb49.png',
    aliases: ['KK Partizan NIS'],
  },
  ASVEL: {
    euroleagueName: 'LDLC ASVEL Villeurbanne',
    code: 'ASV',
    crestUrl: 'https://media-cdn.incrowdsports.com/e33c6d1a-95ca-4dbc-b8cb-0201812104cc.png',
    aliases: ['ASVEL Lyon Villeurbanne'],
  },
  Besiktas: {
    euroleagueName: 'Besiktas Istanbul',
    code: 'BES',
    crestUrl:
      'https://media-cdn.cortextech.io/1dU3kpCqReRp93/1BSdBWIjChWbHH/b05e3c54-e672-4b31-9f86-c91e9de7715c.png',
    aliases: ['Beşiktaş J.K.'],
  },
};

/** Every other name a provider uses for each club, by registry abbreviation. */
export const EUROLEAGUE_TEAM_NAMES: Record<string, readonly string[]> = Object.fromEntries(
  Object.values(EUROLEAGUE_TEAMS).map((team) => [
    team.code,
    [team.euroleagueName, ...(team.aliases ?? [])],
  ]),
);
